import type { DriveItem, ExportFormat } from '../types'

export const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.readonly'
const DRIVE_API = 'https://www.googleapis.com/drive/v3'
const GOOGLE_APPS = 'application/vnd.google-apps.'

type TokenResponse = { access_token?: string; expires_in?: number; error?: string; error_description?: string }
type TokenClient = { requestAccessToken: (config?: { prompt?: string }) => void }
type GoogleIdentity = { accounts: { oauth2: { initTokenClient: (config: { client_id: string; scope: string; callback: (response: TokenResponse) => void; error_callback?: (error: { type?: string; message?: string }) => void }) => TokenClient; revoke: (token: string, callback: () => void) => void } } }

declare global {
  interface Window { google?: GoogleIdentity }
}

export class DriveAuthError extends Error {}
export class DriveTokenExpiredError extends Error {}

function explainGoogleAuthError(code?: string, description?: string, sourceName = 'Google') {
  if (code === 'access_denied') return `Google rechazó el permiso de solo lectura para ${sourceName}. Acepta el acceso o añade tu cuenta como usuario de prueba en Google Cloud.`
  if (code === 'popup_closed_by_user' || code === 'popup_closed') return 'La ventana de Google se cerró antes de terminar. Vuelve a pulsar el botón y completa el consentimiento.'
  if (code === 'origin_mismatch' || code === 'redirect_uri_mismatch') return 'Google rechazó este dominio. Añade exactamente el dominio de la aplicación en Authorized JavaScript origins del cliente OAuth.'
  if (code === 'unauthorized_client' || code === 'invalid_client') return 'Google no reconoce este Client ID en el dominio actual. Comprueba que el cliente sea de tipo aplicación web y que el dominio autorizado coincida.'
  if (code === 'invalid_scope') return `El proyecto de Google no tiene habilitado el permiso solicitado para ${sourceName}. Revisa la API y los scopes configurados.`
  return description || `Google no concedió el acceso de solo lectura a ${sourceName}${code ? ` (${code})` : ''}.`
}

export function isValidClientId(value: string) {
  return /\.apps\.googleusercontent\.com$/.test(value.trim())
}

export async function loadGoogleIdentity() {
  if (window.google?.accounts?.oauth2) return
  await new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-libregoo-gis]')
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(new DriveAuthError('No se pudo cargar el consentimiento oficial de Google.')), { once: true })
      return
    }
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.dataset.libregooGis = 'true'
    script.onload = () => resolve()
    script.onerror = () => reject(new DriveAuthError('No se pudo cargar el consentimiento oficial de Google.'))
    document.head.appendChild(script)
  })
  if (!window.google?.accounts?.oauth2) throw new DriveAuthError('Google no devolvió el módulo de autorización esperado.')
}

export async function authorizeGoogle(clientId: string, scope: string, sourceName: string) {
  if (!isValidClientId(clientId)) throw new DriveAuthError('Introduce un Client ID web válido de Google antes de conectar.')
  await loadGoogleIdentity()
  return new Promise<string>((resolve, reject) => {
    const client = window.google!.accounts.oauth2.initTokenClient({
      client_id: clientId.trim(),
      scope,
      callback: (response) => {
        if (response.error || !response.access_token) reject(new DriveAuthError(explainGoogleAuthError(response.error, response.error_description, sourceName)))
        else resolve(response.access_token)
      },
      error_callback: (error) => reject(new DriveAuthError(explainGoogleAuthError(error.type, error.message, sourceName))),
    })
    client.requestAccessToken({ prompt: 'consent' })
  })
}

export async function authorizeDrive(clientId: string) {
  return authorizeGoogle(clientId, DRIVE_SCOPE, 'Drive')
}

async function driveRequest<T>(token: string, url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, { ...init, headers: { Authorization: `Bearer ${token}`, ...(init?.headers ?? {}) } })
  if (response.status === 401 || response.status === 403) throw new DriveTokenExpiredError('Google pidió renovar el permiso de Drive. Vuelve a conectar y retoma la copia.')
  if (!response.ok) throw new Error(`Drive respondió ${response.status}.`)
  return response.json() as Promise<T>
}

export async function listDriveItems(token: string, includeTrash: boolean, onPage?: (count: number) => void) {
  const items: DriveItem[] = []
  let pageToken: string | undefined
  do {
    const params = new URLSearchParams({
      pageSize: '1000',
      orderBy: 'modifiedTime desc',
      fields: 'nextPageToken,files(id,name,mimeType,size,modifiedTime,createdTime,parents,trashed,ownedByMe,sharedWithMeTime,driveId,webViewLink)',
      spaces: 'drive',
      includeItemsFromAllDrives: 'true',
      supportsAllDrives: 'true',
    })
    if (!includeTrash) params.set('q', 'trashed = false')
    if (pageToken) params.set('pageToken', pageToken)
    const page = await driveRequest<{ nextPageToken?: string; files?: DriveItem[] }>(token, `${DRIVE_API}/files?${params.toString()}`)
    items.push(...(page.files ?? []))
    onPage?.(items.length)
    pageToken = page.nextPageToken
  } while (pageToken)
  return items
}

const exportsByMime: Record<string, Record<ExportFormat, { mime: string; extension: string }>> = {
  [`${GOOGLE_APPS}document`]: { pdf: { mime: 'application/pdf', extension: '.pdf' }, office: { mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', extension: '.docx' } },
  [`${GOOGLE_APPS}spreadsheet`]: { pdf: { mime: 'application/pdf', extension: '.pdf' }, office: { mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', extension: '.xlsx' } },
  [`${GOOGLE_APPS}presentation`]: { pdf: { mime: 'application/pdf', extension: '.pdf' }, office: { mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', extension: '.pptx' } },
}

export function isFolder(item: DriveItem) { return item.mimeType === `${GOOGLE_APPS}folder` }
export function isGoogleNative(item: DriveItem) { return item.mimeType.startsWith(GOOGLE_APPS) && !isFolder(item) }

export async function downloadDriveItem(token: string, item: DriveItem, exportFormat: ExportFormat) {
  let url: string
  let name = item.name
  if (isGoogleNative(item)) {
    const exportOption = exportsByMime[item.mimeType]?.[exportFormat]
    if (!exportOption) throw new Error(`No se puede exportar «${item.name}» en este MVP.`)
    const params = new URLSearchParams({ mimeType: exportOption.mime })
    url = `${DRIVE_API}/files/${encodeURIComponent(item.id)}/export?${params.toString()}`
    if (!name.toLowerCase().endsWith(exportOption.extension)) name += exportOption.extension
  } else {
    url = `${DRIVE_API}/files/${encodeURIComponent(item.id)}?alt=media&supportsAllDrives=true`
  }
  const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
  if (response.status === 401 || response.status === 403) throw new DriveTokenExpiredError('Google pidió renovar el permiso de Drive. Vuelve a conectar y retoma la copia.')
  if (!response.ok) throw new Error(`No se pudo descargar «${item.name}» (${response.status}).`)
  return { name, blob: await response.blob() }
}

export function revokeDrive(token: string) {
  if (window.google?.accounts?.oauth2) window.google.accounts.oauth2.revoke(token, () => undefined)
}
